import React, { useContext as useContextCore, useMemo } from 'react'
import type { UseListResult, UseListWithSelection } from '@ztechapps/common.hook'
import { useListWithSelection } from '@ztechapps/common.hook'
import { usePurchaseOrderList } from '@ztechapps/purchase-order.common'
import type { PurchaseOrder, RouteDef } from '@ztechapps/common.contract'

export type PurchaseOrderListContextProps = {
  filterCategory?: string
  children: React.ReactNode
  purchaseItemRoute: RouteDef
  vendorItemRoute: RouteDef
  companyItemRoute: RouteDef
  // Live-updates the list; off unless the screen that shows it asks for it.
  subscribeToUpdates?: boolean
}
export type PurchaseOrderListProps = Omit<PurchaseOrderListContextProps, 'children'>

export type PurchaseOrderListContextType = {
  filterCategory: string
  selectedOrderIds: string[]
  selectedOrders: PurchaseOrder[]
  orderList: UseListResult<PurchaseOrder>
  orderListWithSelection: UseListWithSelection<PurchaseOrder>
  onRefresh: () => Promise<void>
} & PurchaseOrderListProps

const context = React.createContext({} as PurchaseOrderListContextType)
const CoreProvider = context.Provider
const defaultFilterCategory = 'purchaseOrders-FilterCategory'

export const PurchaseOrderListProvider = ({
  children,
  filterCategory,
  subscribeToUpdates = false,
  ...rest
}: PurchaseOrderListContextProps) => {
  //set the default filterCategory
  filterCategory = filterCategory ?? defaultFilterCategory

  const orderList = usePurchaseOrderList({
    listActionOptions: { storageKey: filterCategory },
    subscribeToUpdates,
  })
  const orderListWithSelection = useListWithSelection(orderList?.list)
  const selectedOrderIds = useMemo(() => {
    return orderListWithSelection.selectedList.map((item) => item._id)
  }, [orderListWithSelection])
  const selectedOrders = orderListWithSelection.selectedList

  const onRefresh = async () => {
    await orderList.refetch()
  }

  return (
    <CoreProvider
      value={{
        ...rest,
        orderList,
        orderListWithSelection,
        filterCategory,
        selectedOrders,
        selectedOrderIds,
        onRefresh,
      }}
    >
      {children}
    </CoreProvider>
  )
}

export const usePurchaseOrderListContext = () => useContextCore(context)
